/**
 * Hook d'enregistrement audio (dictée du corrigé). Audio SEUL — réutilise la
 * mécanique MediaRecorder du partage de défi, sans piste vidéo. L'enregistrement
 * vit en mémoire ; il est remis à l'appelant sous forme de `Blob` au `stop()` et
 * n'est jamais stocké (la transcription suit, puis l'audio est jeté).
 *
 * Mesure en plus le NIVEAU sonore en direct (AnalyserNode) : l'appelant affiche
 * un témoin pour vérifier que le micro capte, et on sait au `stop()` si un
 * signal a été détecté (`hadSignal`) — sinon on évite un appel Whisper inutile.
 *
 * Machine d'état : idle → recording → (stop) → idle (le Blob est renvoyé).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { pickRecommendedMic, isLikelyVirtualMic } from './mic-utils';

export type RecorderState = 'idle' | 'recording';

/** Seuil RMS (0..1) au-dessus duquel on considère qu'il y a de la voix. */
const SIGNAL_THRESHOLD = 0.025;

/** Clé localStorage du micro choisi (persiste entre sessions). */
const MIC_STORAGE_KEY = 'jeraisonne.dictation.micDeviceId';

function loadStoredDeviceId(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(MIC_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function storeDeviceId(id: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (id) localStorage.setItem(MIC_STORAGE_KEY, id);
    else localStorage.removeItem(MIC_STORAGE_KEY);
  } catch {
    /* ignore (mode privé / quota) */
  }
}

/** Formats audio candidats de la dictée, par préférence (le 1er supporté gagne). */
const DEFAULT_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

function pickAudioMime(candidates: readonly string[]): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return candidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
}

export interface StopResult {
  blob: Blob | null;
  /** Un signal sonore a-t-il été détecté pendant l'enregistrement ? */
  hadSignal: boolean;
}

export interface UseAudioRecorder {
  state: RecorderState;
  seconds: number;
  /** Niveau sonore courant (0..1), pour un VU-mètre en direct. */
  level: number;
  error: string | null;
  /** Micros disponibles (libellés peuplés après la 1re autorisation). */
  devices: MediaDeviceInfo[];
  /** Périphérique d'entrée choisi (null = micro par défaut du système). */
  deviceId: string | null;
  /** Choisit le micro à utiliser au prochain enregistrement. */
  setDeviceId: (id: string | null) => void;
  /** Libellé du micro réellement capté lors du dernier enregistrement. */
  activeLabel: string | null;
  /** (Re)liste les périphériques audio. */
  refreshDevices: () => Promise<void>;
  /** Demande le micro et démarre l'enregistrement. */
  start: () => Promise<void>;
  /** Arrête et renvoie l'audio enregistré + s'il y avait du son. */
  stop: () => Promise<StopResult>;
  /** Coupe le micro sans produire de blob (annulation). */
  cancel: () => void;
}

export interface AudioRecorderOptions {
  /**
   * formats essayés, par préférence (défaut : webm d'abord, pour Whisper).
   * La voix des animations préfère le m4a, lu sur iPhone.
   */
  mimeTypes?: readonly string[];
}

export function useAudioRecorder(opts: AudioRecorderOptions = {}): UseAudioRecorder {
  const mimeTypes = opts.mimeTypes ?? DEFAULT_MIME_TYPES;
  const mimeTypesRef = useRef(mimeTypes);
  mimeTypesRef.current = mimeTypes;
  const [state, setState] = useState<RecorderState>('idle');
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceIdState] = useState<string | null>(() => loadStoredDeviceId());
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const deviceIdRef = useRef<string | null>(loadStoredDeviceId());
  // L'utilisateur a-t-il choisi un micro explicitement (ce session ou persisté) ?
  const explicitRef = useRef<boolean>(loadStoredDeviceId() !== null);
  const autoPickDoneRef = useRef(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mimeRef = useRef<string>('');
  // VU-mètre
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const hadSignalRef = useRef(false);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setLevel(0);
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const inputs = list.filter((d) => d.kind === 'audioinput');
      setDevices(inputs);
      // Aucun choix explicite → on évite le « défaut système » (souvent un
      // périphérique VIRTUEL type Teams qui ne capte rien) et on sélectionne
      // automatiquement le 1er micro physique. Une seule fois par session.
      if (!explicitRef.current && !autoPickDoneRef.current) {
        const rec = pickRecommendedMic(inputs);
        if (rec) {
          autoPickDoneRef.current = true;
          deviceIdRef.current = rec;
          setDeviceIdState(rec);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setDeviceId = useCallback((id: string | null) => {
    explicitRef.current = true; // choix délibéré : on ne ré-auto-sélectionne plus
    deviceIdRef.current = id;
    setDeviceIdState(id);
    storeDeviceId(id);
  }, []);

  // Liste initiale + mise à jour quand un micro est branché/débranché.
  useEffect(() => {
    void refreshDevices();
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const handler = () => void refreshDevices();
    md.addEventListener('devicechange', handler);
    return () => md.removeEventListener('devicechange', handler);
  }, [refreshDevices]);

  useEffect(() => cleanup, [cleanup]);

  /** Met en place l'analyse de niveau sonore sur le flux micro. */
  const startMeter = useCallback((stream: MediaStream) => {
    const Ctx =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);

    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i]! - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      if (rms > SIGNAL_THRESHOLD) hadSignalRef.current = true;
      setLevel(Math.min(1, rms * 4));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    hadSignalRef.current = false;
    try {
      const baseAudio: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };

      /** Ouvre un flux ; `exact` force le micro choisi (pas de retour au défaut). */
      const open = (id: string | null): Promise<MediaStream> =>
        navigator.mediaDevices.getUserMedia({
          audio: id ? { ...baseAudio, deviceId: { exact: id } } : baseAudio,
        });

      let stream: MediaStream;
      try {
        stream = await open(deviceIdRef.current);
      } catch {
        // Micro mémorisé indisponible (débranché) → on nettoie et on retombe
        // sur le défaut système (puis l'auto-correction ci-dessous s'applique).
        deviceIdRef.current = null;
        setDeviceIdState(null);
        storeDeviceId(null);
        stream = await open(null);
      }

      let track = stream.getAudioTracks()[0];

      // AUTO-CORRECTION : si le flux a atterri sur un micro VIRTUEL (Teams, OBS…)
      // SANS qu'un périphérique précis ait été forcé (deviceId null = « défaut
      // système »), on bascule sur le 1er micro PHYSIQUE et on rouvre — il ne
      // capte jamais le virtuel. Un choix explicite d'un device précis est respecté.
      if (
        deviceIdRef.current === null &&
        track?.label &&
        isLikelyVirtualMic(track.label) &&
        navigator.mediaDevices?.enumerateDevices
      ) {
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          const inputs = list.filter((d) => d.kind === 'audioinput');
          setDevices(inputs);
          const physical = pickRecommendedMic(inputs);
          if (physical && physical !== track.getSettings().deviceId) {
            stream.getTracks().forEach((t) => t.stop());
            autoPickDoneRef.current = true;
            deviceIdRef.current = physical;
            setDeviceIdState(physical);
            stream = await open(physical);
            track = stream.getAudioTracks()[0];
          }
        } catch {
          /* on garde le flux courant si la bascule échoue */
        }
      }

      streamRef.current = stream;
      setActiveLabel(track?.label || null);
      // Libellés désormais disponibles (autorisation accordée) → rafraîchir.
      void refreshDevices();
      if (track && (track.muted || track.enabled === false)) {
        setError('Le micro est coupé (muet). Réactive-le puis réessaie.');
      }

      mimeRef.current = pickAudioMime(mimeTypesRef.current);
      const recorder = mimeRef.current
        ? new MediaRecorder(stream, { mimeType: mimeRef.current })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      // Timeslice 1 s : flush régulier des chunks (plus robuste que l'unique
      // flush au stop, qui peut produire un blob vide sur certains navigateurs).
      recorder.start(1000);
      recorderRef.current = recorder;
      setSeconds(0);
      setState('recording');
      startMeter(stream);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      cleanup();
      setState('idle');
      setError(
        "Micro indisponible ou refusé. Autorise l'accès au microphone dans le navigateur (icône 🎤 dans la barre d'adresse), puis réessaie.",
      );
    }
  }, [cleanup, startMeter, refreshDevices]);

  const stop = useCallback((): Promise<StopResult> => {
    const recorder = recorderRef.current;
    if (!recorder) {
      setState('idle');
      return Promise.resolve({ blob: null, hadSignal: false });
    }
    return new Promise<StopResult>((resolve) => {
      recorder.onstop = () => {
        const blob = chunksRef.current.length
          ? new Blob(chunksRef.current, { type: mimeRef.current || 'audio/webm' })
          : null;
        const hadSignal = hadSignalRef.current;
        cleanup();
        setState('idle');
        resolve({ blob, hadSignal });
      };
      recorder.stop();
    });
  }, [cleanup]);

  const cancel = useCallback(() => {
    cleanup();
    setState('idle');
    setSeconds(0);
    setError(null);
  }, [cleanup]);

  return {
    state,
    seconds,
    level,
    error,
    devices,
    deviceId,
    setDeviceId,
    activeLabel,
    refreshDevices,
    start,
    stop,
    cancel,
  };
}
